import moment from 'moment'
import 'moment/locale/de'
import { isFinite, isObject } from 'lodash'

moment.locale('de')

export function isNumberLike(suspectedNumber) {
  return isFinite(suspectedNumber * 1)
}

export function ensureNumber(suspectedNumber) {
  return isNumberLike(suspectedNumber)
    ? suspectedNumber * 1
    : undefined
}

export function camelCase(str) {
  if (typeof str !== 'string') {
    return str
  }

  if (/^\S{2}-\S{2}$/.test(str)) {
    return str
  }

  return str.replace(/\S[-_]([\S])/gi, (match) => {
    return `${match[0]}${match[2].toUpperCase()}`
  })
}

export function pickFromList(list, filter) {
  return list
    .find((entry) => entry[filter.key] === filter.value) || {}
}

export function getConfig(key) {
  return window.APP_CONFIG[key]
}

export function listGetById(list, id) {
  const item = (window.APP_CONFIG[list] || [])
    .find((i) => (i.id * 1 === id * 1))
  return item ? item.title : null
}

export function camelCaseJSON(data = null) {

  if (!(data && (isObject(data) || Array.isArray(data)))) {
    return data
  }

  const newData = Array.isArray(data) ? [] : {}
  Object.keys(data).forEach((key) => {
    const val = data[key]

    const camelKey = key === 'i18n'
      ? key
      : camelCase(key)

    newData[camelKey] = (Array.isArray(val) || isObject(val))
      ? camelCaseJSON(val)
      : val
  })

  return newData
}

/**
 * Gets the value at the given `keypath` from the Object `obj`. So it provides
 * easy deep access to an object.
 * @example
 * const obj = {
 * 'this' : {
 *   'is' : {
 *    'a' : {
 *      'demo': 'yay'
 *     }
 *   }
 * }
 * deepGet(obj, 'this.is.a.demo') === 'yay'
 * @param {Object} obj - The object to get the value from.
 * @param {String} keypath - The path of keys to the deeply nested value.
 * @returns {*} - The value at the given `keypath` or `undefined` if any of
 * the keys does not exist on the object.
 */
export function deepGet(
  obj?: Object,
  keypath?: string,
  defaultValue?: any
) {

  if (!obj) {
    return defaultValue || undefined
  }

  return keypath.split('.')
    .map((currKey) => deepGet.unescapeKey(currKey))
    .reduce((memo, currKey) => (
      memo
    // currKey::Object.prototype.toString() === '[object Object]' &&
    && currKey in memo
        ? memo[currKey]
        : undefined
    ), obj)

}

deepGet.escapeKey = (key) => {
  return key.replace(/\./g, 'DEEP_GET_ESCAPED_KEY')
}

deepGet.unescapeKey = (key) => {
  return key.replace(/DEEP_GET_ESCAPED_KEY/g, '.')
}

/**
 * Sets the value at the given `keypath` from the Object `obj`. So it provides
 * an easy way to update a nested value on a document. If any of the keys in the
 * `keypath` does not exist, a new entry of type `Object` will be created at the
 * position in the key path within `obj`.
 * @param {Object} obj - The object to get the value from.
 * @param {String} keypath - The path of keys to the deeply nested value.
 * @param {*} val - The new value to set at the given keypath.
 * @returns {Object} - The original object `obj`.
 */
export function deepSet(obj, keypath, val) {

  if (!obj) {

    throw new Error('`deepSet` expects an Object as first parameter.')

  }

  const keys = keypath.split('.')
  const lastKey = keys.pop()

  const subObj = keys
    .reduce((memo, currKey) => {
      if (memo && currKey in memo) {
        return memo[currKey]
      }
      memo[currKey] = {}
      return memo[currKey]
    }, obj)

  if (subObj) {
    subObj[lastKey] = val
  }

  return obj

}

export function formatDate(date, format) {

  if (!date || date === '0000-00-00') {
    return ''
  }

  const expectedData = new Date(date)
  if (Number.isNaN(expectedData.valueOf())) {
    return `Ungüliges Datum: ${date}`
  }

  if (format === 'time') {
    format = 'DD.MM.YYYY<br/>HH:mm [Uhr]'
  }

  else if (format === 'time-slim') {
    format = 'DD.MM.YYYY, HH:mm [Uhr]'
  }

  else if (format === 'input') {
    format = 'YYYY-MM-DD'
  }

  return moment(date).format(format || 'DD.MM.YYYY')
}

export function formatMonth(date) {
  return formatDate(date, 'MMMM')
}

export function formatNumber(number) {
  if (!number || number * 1 === 0) {
    return '0.00'
  }
  number *= 100
  number += ''
  return `${number.substr(0, number.length - 2)}.${number.substr(number.length - 2)}`
}

export function roundNumber(number) {
  return window.parseInt(number * 10) / 10
}

const imageSizeMap = {
  mini: 100,
  small: 200,
  banner: 600,
  orig: 1200
}

export function makeDataUrl(image, size) {

  const { APP_CONFIG: { API_HOST, APP_URL, DATA_PATH } } = window

  if (image && ('dataURL' in image || (image.file && 'dataURL' in image.file))) {
    return image.dataURL || image.file.dataURL
  }

  const dataUrl = image.url || image
  if (!/(jpg|png)$/.test(dataUrl)) {
    return `${API_HOST}${DATA_PATH}${dataUrl}`
  }

  const baseURL = `${API_HOST}${APP_URL}`
  let url = `${baseURL}assets/images/default.jpg`
  size = size || 'orig'

  if (!(size in imageSizeMap)) {
    size = 'orig'
  }

  size = imageSizeMap[size]

  if (image && image.url) {
    if (image.url.indexOf('http') === 0) {
      url = image.url
    }
    else {
      url = image.url
        .replace(/\/([^/]+$)/, `/${size}_$1`)
        .replace(/\.png$/, '.jpg')
      url = `${API_HOST}${DATA_PATH}${url}`
    }
  }

  return url

}

export const isNotEmpty = (val) => {
  if (!val) {
    return false
  }

  if (typeof val === 'string') {
    if (!Number.isNaN(val * 1)) {
      val *= 1
    }
    else {
      return !!(val.trim && val.trim().length)
    }
  }

  if (typeof val === 'number') {
    return !!val
  }

  return !!val
}

export const halfCollection = (collection, key) => {
  let index = parseInt(collection.length / 2, 10)
  const char = collection[index][key][0]

  index += 1

  if (index < collection.length) {
    let nextChar = collection[index][key][0]

    while (char === nextChar && index < collection.length - 1) {
      index += 1
      nextChar = collection[index][key][0]
    }
  }

  return [
    collection.slice(0, index),
    collection.slice(index)
  ]
}

export const createIndexCollection = (collection, opts) => {
  return collection
    .reduce((memo, item) => {
      const newChar = item[opts.key][0] || ' '

      if (opts.lastChar !== newChar) {
        memo.push(newChar)
        opts.lastChar = newChar
      }

      memo.push(item)
      return memo
    }, [])
  // .sort((a, b) => a.sanitizedOrderTitle.localeCompare(b.sanitizedOrderTitle))

}



// WEBPACK FOOTER //
// ./src/shared/utils.js